import React, { useState, useMemo, useEffect } from "react";
import { Classes, Colors, Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { Tooltip2 } from "@blueprintjs/popover2";
import { Box, quarks, Avatar } from "ui-components";
import { userQuery } from "ui-io/queries/users/user-query";
import { TodoNoteT, ReferenceT } from "ui-io/task-io";
import moment from "moment";
import { useQuery, useMutation, useQueryClient } from "react-query";
import styled from "styled-components";
import useSession from "ui-io/use-session";
import { taskUnPinCommentMutation } from "ui-io/queries/tasks/task-pin-comment";
import { unpinBrandNotes } from "ui-io/queries/configs/unpin-brand-notes";

import normalizeUserQueryData from "../../utils/normalize-user-query-data";
import Attachment from "../task/discussion-thread/attachment";
import AttachmentDialog from "../task/discussion-thread/attachment-dialog";
import { ActiveAttachmentT } from "../task/discussion-thread";
import toasts from "../../utils/toasts";
import { TASKS_QUERY_FILTER } from "ui-io/queries/tasks/task-query";

const BrandNote = ({
  note,
  attachments,
}: {
  note: TodoNoteT;
  attachments?: ReferenceT[];
}) => {
  const { session } = useSession();
  const currentUsername = session?.username;
  const [attachmentOpened, setAttachmentOpened] = useState<ActiveAttachmentT>();
  const { data, isLoading, isError } = useQuery(userQuery(note.user));
  const { email, displayName } = normalizeUserQueryData({
    data,
    username: note.user,
  });

  const date = note.timestamp !== undefined ? note.timestamp : Date.now();
  const timeAgo = moment(date).fromNow();

  const queryClient = useQueryClient();

  const handleToggleIsAttachmentOpen = (nextAttachment: ActiveAttachmentT) => {
    setAttachmentOpened(nextAttachment);
  };

  const handleCloseIsAttachmentOpen = () => {
    setAttachmentOpened(undefined);
  };

  const getAttachments = () => {
    const result =
      attachments &&
      attachments.map((attachment, index) => {
        return (
          <Attachment
            key={index}
            attachment={attachment}
            isUsersComment={false}
            handleToggleIsAttachmentOpen={handleToggleIsAttachmentOpen}
            uploadTimeStamp={-1}
            isDeleteButton={false}
          />
        );
      });
    return <>{result}</>;
  };

  const unPinCommentMutation = useMutation(taskUnPinCommentMutation, {
    onSuccess: () => {
      toasts.success("Successfully un-pinned the comment!");
      queryClient.refetchQueries(["LIST_COMPANY_NOTES"]);
      queryClient.refetchQueries([TASKS_QUERY_FILTER, note.taskId]);
    },
    onError: () => {
      toasts.error("Failed to un-pin the comment!");
    },
  });
  const unPinBrandCommentMutation = useMutation(unpinBrandNotes, {
    onSuccess: () => {
      toasts.success("Successfully un-pinned the comment!");
      queryClient.refetchQueries(["LIST_COMPANY_NOTES"]);
      queryClient.refetchQueries([TASKS_QUERY_FILTER, note.taskId]);
    },
    onError: () => {
      toasts.error("Failed to un-pin the company notes!");
    },
  });

  const handlePinComments = () => {
    unPinBrandCommentMutation.mutate({
      noteId: note.noteId || "",
    });
  };
  const isUsersComment = currentUsername === note.user;
  return (
    <BrandNoteCard className={Classes.ELEVATION_0}>
      <Avatar
        email={email || "yantrix@gmail.com"}
        profileUrl={""}
        onlyAvatar={true}
        displayName={displayName || currentUsername}
        isCurrentUser={isUsersComment}
        isLoading={isLoading}
        size={"30px"}
        position="start"
      />
      <Pinned onClick={handlePinComments}>
        <Icon icon={IconNames.PIN} />
      </Pinned>
      <Box flexDir="col" p={1} style={{ width: "100%" }}>
        <Box flexDir="row" justify="between" mb="xsmall">
          <Tooltip2 content={email} disabled={isLoading || isError}>
            <TinyText
              style={{
                textTransform: "capitalize",
                fontSize: "13px",
                marginRight: 6,
                ...(isError && { fontStyle: "italic" }),
              }}
              className={isLoading ? Classes.SKELETON : ""}
            >
              {displayName}
            </TinyText>
          </Tooltip2>
          <Tooltip2 content={moment(date).format("lll")}>
            <TinyText>{timeAgo}</TinyText>
          </Tooltip2>
        </Box>
        <p>{note.text}</p>
        <p>{getAttachments()}</p>
      </Box>
      <AttachmentDialog
        attachmentOpened={attachmentOpened}
        handleCloseIsAttachmentOpen={handleCloseIsAttachmentOpen}
      />
    </BrandNoteCard>
  );
};

const BrandNotesList = ({
  notes,
  taskId,
  companyID,
}: {
  notes: TodoNoteT[];
  taskId?: string;
  companyID?: string;
}) => {
  return (
    <Box flexDir="col">
      {notes.map((note) => {
        return (
          <BrandNote
            key={note.timestamp}
            note={note}
            attachments={note.references ?? []}
          />
        );
      })}
    </Box>
  );
};
export default BrandNotesList;

const BrandNoteCard = styled.div`
  font-size: 0.95em;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: ${quarks.margins.xsmall};
  padding: ${quarks.margins.medium};
  background-color: ${(props) => {
    return props.theme.darkMode ? Colors.DARK_GRAY2 : Colors.LIGHT_GRAY5;
  }};
  max-width: 500px;
`;

const TinyText = styled.div`
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 6px;
  color: ${Colors.BLUE4};
`;
const Pinned = styled.div`
  padding-left: 1%;
  z-index: 2;
  position: absolute;
  right: 0;
  top: 0;
  color: ${Colors.RED5};
  &:hover {
    cursor: pointer;
    transform: scale(1.2);
  }
`;
